// frontend/src/layouts/MainLayout.tsx
// UX-01 - Pricing private shell aligned visually with Industrial Cost.
// PRC-044: vuelve el menú lateral de antes de PRC-034 (plegado, se despliega al
// pasar el ratón). De PRC-034 solo se conserva el menú de la cuenta al pulsar el
// nombre (PricingAccountMenu) y que no haya barra superior. Sin Planificación (PRC-039).
import React, { useEffect, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';
import { Link as RouterLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  alpha,
  AppBar,
  Avatar,
  Box,
  Button,
  Container,
  CssBaseline,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  GlobalStyles,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AccountCircle from '@mui/icons-material/AccountCircle';
import CloseRounded from '@mui/icons-material/CloseRounded';
import MenuRounded from '@mui/icons-material/MenuRounded';
import { useAuth } from '../context/AuthContext';
// PRC-045: iconos del menú de PRC-034 (boceto G2).
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import TrackChangesRoundedIcon from '@mui/icons-material/TrackChangesRounded';
import TrialBanner from '../modules/pricingJourney/components/TrialBanner';
import { CancelledSubscriptionBanner } from '../modules/pricingJourney/components/MySubscriptionPanel';
import { usePricingOnboarding } from '../modules/pricingJourney/onboarding';
import { LayoutProvider } from '../context/LayoutContext';
import Footer from '../components/Footer';
import PricingAccountMenu from '../modules/pricingJourney/components/PricingAccountMenu';
import PageTransition, { MENU_NAVIGATION_STATE } from '../modules/pricingJourney/components/PageTransition';
import { ThemeProvider, type Theme } from '@mui/material/styles';
import Grow from '@mui/material/Grow';

// PRC-031: diálogos de Pricing con zoom ligero y fundido (200 ms); el resto del tema no cambia.
const pricingDialogTheme = (outer: Theme): Theme => ({
  ...outer,
  components: {
    ...outer.components,
    MuiDialog: {
      ...outer.components?.MuiDialog,
      defaultProps: {
        ...outer.components?.MuiDialog?.defaultProps,
        TransitionComponent: Grow,
        transitionDuration: 200,
      },
    },
  },
});

type PricingNavItemProps = {
  label: string;
  icon: React.ReactNode;
  selected: boolean;
  onClick: () => void;
  badge?: string;
};

// PRC-045: cada página es una línea directa con su icono.
function PricingNavItem({ label, icon, selected, onClick, badge }: PricingNavItemProps) {
  return (
    <ListItemButton
      onClick={onClick}
      selected={selected}
      aria-current={selected ? 'page' : undefined}
      sx={{
        minHeight: 44,
        borderRadius: 2.5,
        px: 2,
        gap: 1.5,
        mb: 0.5,
        bgcolor: selected ? alpha('#38bdf8', 0.12) : 'transparent',
        border: `1px solid ${selected ? alpha('#38bdf8', 0.18) : 'transparent'}`,
        color: selected ? '#f8fafc' : '#94a3b8',
        transition: 'all 0.18s ease',
        '&.Mui-selected, &.Mui-selected:hover': {
          bgcolor: alpha('#38bdf8', 0.12),
          color: '#f8fafc',
        },
        '&:hover': {
          bgcolor: selected ? alpha('#38bdf8', 0.14) : alpha('#ffffff', 0.03),
          color: '#e2e8f0',
        },
      }}
    >
      <Box component="span" aria-hidden sx={{ display: 'flex', color: selected ? '#7dd3fc' : '#94a3b8', '& .MuiSvgIcon-root': { fontSize: 20 } }}>
        {icon}
      </Box>
      <ListItemText
        primary={label}
        primaryTypographyProps={{
          fontSize: '0.95rem',
          fontWeight: selected ? 800 : 700,
          lineHeight: 1.2,
        }}
      />
      {badge && (
        <Box component="span" sx={{ fontSize: 11, fontWeight: 800, color: '#0f172a', bgcolor: '#fcd34d', borderRadius: 999, px: 1, py: '2px' }}>
          {badge}
        </Box>
      )}
    </ListItemButton>
  );
}

type PricingRailButtonProps = {
  label: string;
  selected: boolean;
  icon: React.ReactNode;
  onClick: () => void;
};

function PricingRailButton({ label, icon, selected, onClick }: PricingRailButtonProps) {
  return (
    // PRC-028: con la barra plegada, cada icono muestra su nombre al pasar el ratón.
    <Tooltip title={label} placement="right">
    <IconButton
      aria-label={label}
      aria-current={selected ? 'page' : undefined}
      onClick={onClick}
      sx={{
        width: 46,
        height: 46,
        mb: 0.5,
        p: 0,
        borderRadius: 2.5,
        color: selected ? '#f8fafc' : '#94a3b8',
        bgcolor: selected ? alpha('#38bdf8', 0.16) : 'transparent',
        border: `1px solid ${selected ? alpha('#38bdf8', 0.24) : 'transparent'}`,
        transition: 'all 0.18s ease',
        '& .MuiSvgIcon-root': { fontSize: 22 },
        '&:hover': {
          color: '#e2e8f0',
          bgcolor: selected ? alpha('#38bdf8', 0.18) : alpha('#ffffff', 0.05),
          borderColor: alpha('#38bdf8', 0.2),
        },
      }}
    >
      {icon}
    </IconButton>
    </Tooltip>
  );
}

const isAt = (pathname: string, route: string) =>
  pathname === route || pathname.startsWith(`${route}/`);

const isOperationsPath = (pathname: string) =>
  ['/operations', '/data-upload', '/data-quality', '/historical-costs', '/connections', '/retraining', '/enviar-archivos']
    .some((route) => isAt(pathname, route));

const isOptimizationPath = (pathname: string) =>
  ['/optimization-center', '/optimization', '/batch-optimization']
    .some((route) => isAt(pathname, route));

const isAccountPath = (pathname: string) =>
  ['/profile', '/team', '/subscription', '/planes'].some((route) => isAt(pathname, route));

const privateLayoutPadding = { xs: 2, sm: 3 };

const MainLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { signOut, user, isAuthenticated, isPricingAdmin, pricingAccess } = useAuth();

  // PRC-019: «Primeros pasos» en el menú mientras no estén los cinco; después, en el menú de usuario.
  // Solo con acceso: el servicio exige acceso y un 403 recargaría el contexto (bucle en /subscription).
  const { data: onboarding } = usePricingOnboarding(isAuthenticated && pricingAccess?.has_access === true);
  const firstStepsPending = !!onboarding && onboarding.done < onboarding.total;
  // PRC-024: al completarse el quinto paso, aviso breve una sola vez donde esté el usuario.
  const { enqueueSnackbar } = useSnackbar();
  const wasFirstStepsPending = useRef(false);
  useEffect(() => {
    if (!onboarding) return;
    if (firstStepsPending) {
      wasFirstStepsPending.current = true;
    } else if (wasFirstStepsPending.current) {
      wasFirstStepsPending.current = false;
      enqueueSnackbar('Has completado la guía de inicio.', { variant: 'success' });
    }
  }, [onboarding, firstStepsPending, enqueueSnackbar]);
  const shouldUseCompactSidebar = useMediaQuery(
    '(max-width:1199.95px), (max-width:1535.95px) and (any-pointer:coarse)',
  );

  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [desktopSidebarOpen, setDesktopSidebarOpen] = useState(false);
  const [desktopSidebarContentVisible, setDesktopSidebarContentVisible] = useState(false);
  const [userMenuPosition, setUserMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);

  const pathname = location.pathname;
  const operationsActive = isOperationsPath(pathname);
  const optimizationActive = isOptimizationPath(pathname);
  const accountActive = isAccountPath(pathname);
  // PRC-028: el botón de usuario se marca en sus pantallas (y en la guía cuando ya está completa).
  const userMenuActive = accountActive || (isAt(pathname, '/primeros-pasos') && !firstStepsPending);
  // PRC-028: las fichas marcan Explorer. PRC-049: Control comercial (y sus avisos
  // y Revisiones programadas) tiene entrada propia en el menú.
  const overviewActive = isAt(pathname, '/margin-center/overview');
  const commercialControlActive = isAt(pathname, '/margin-center/control-comercial');
  const explorerActive = ['/margin-center/explorer', '/margin-center/customers', '/margin-center/products'].some((route) => isAt(pathname, route));
  const isDesktopSidebarExpanded = !shouldUseCompactSidebar &&
    (desktopSidebarOpen || Boolean(userMenuPosition));
  const isDesktopSidebarContentVisible =
    shouldUseCompactSidebar || desktopSidebarContentVisible;
  const userEmail = user?.email || '';
  const userName = user?.full_name?.trim() || userEmail || 'Usuario';
  // PRC-020: el plan se nombra «Prueba», «Growth» o «Autonomy»; nunca discovery ni free.
  const planLabel = pricingAccess?.state === 'trial'
    ? 'Prueba'
    : pricingAccess?.state === 'paid' && user?.tier_id === 'growth'
      ? 'Growth'
      : pricingAccess?.state === 'paid' && user?.tier_id === 'autonomy'
        ? 'Autonomy'
        : null;
  const userSubtitle = planLabel ? `Plan ${planLabel}` : 'Mi cuenta';

  useEffect(() => {
    if (shouldUseCompactSidebar || !isDesktopSidebarExpanded) {
      setDesktopSidebarContentVisible(false);
      return;
    }
    const timeout = window.setTimeout(() => setDesktopSidebarContentVisible(true), 90);
    return () => window.clearTimeout(timeout);
  }, [isDesktopSidebarExpanded, shouldUseCompactSidebar]);

  useEffect(() => {
    setMobileSidebarOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!mobileSidebarOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMobileSidebarOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [mobileSidebarOpen]);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const goTo = (route: string) => {
    setMobileSidebarOpen(false);
    // PRC-031: cambiar de sección desde el menú es un fundido, no «Volver».
    navigate(route, { state: MENU_NAVIGATION_STATE });
  };

  // PRC-045: secciones con título pequeño (solo con el menú desplegado) y una
  // línea por página. Mismas páginas y rutas que antes.
  const navSections: Array<{ title: string; items: Array<{ label: string; route: string; icon: React.ReactNode; selected: boolean; badge?: string }> }> = [
    ...(firstStepsPending ? [{
      title: 'Empezar',
      items: [{ label: 'Primeros pasos', route: '/primeros-pasos', icon: <FlagOutlinedIcon />, selected: isAt(pathname, '/primeros-pasos'), badge: `${onboarding!.done}/${onboarding!.total}` }],
    }] : []),
    {
      title: 'Margin Center',
      items: [
        { label: 'Overview', route: '/margin-center/overview', icon: <GridViewRoundedIcon />, selected: overviewActive },
        { label: 'Explorer', route: '/margin-center/explorer', icon: <SearchRoundedIcon />, selected: explorerActive },
        { label: 'Control comercial', route: '/margin-center/control-comercial', icon: <TrackChangesRoundedIcon />, selected: commercialControlActive },
      ],
    },
    {
      title: 'Precios',
      items: [{ label: 'Optimización', route: '/optimization-center', icon: <BoltRoundedIcon />, selected: optimizationActive }],
    },
    {
      title: 'Datos',
      items: [{ label: 'Operaciones', route: '/operations', icon: <FileUploadOutlinedIcon />, selected: operationsActive }],
    },
  ];

  const handleCloseUserMenu = () => setUserMenuPosition(null);
  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    // PRC-047: el menú arranca en el borde izquierdo del menú lateral, no donde se pulsa.
    const sidebar = event.currentTarget.closest('nav');
    const left = sidebar ? sidebar.getBoundingClientRect().left : event.clientX;
    setUserMenuPosition({ top: event.clientY, left });
  };
  const handleLogout = async () => {
    handleCloseUserMenu();
    setMobileSidebarOpen(false);
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('MainLayout: Error durante signOut:', error);
    }
  };

  // Retain the original unauthenticated MainLayout fallback unchanged.
  const appBarStyles = {
    backgroundColor: isScrolled
      ? alpha('#0f172a', 0.9)
      : alpha('#0f172a', 0.5),
    backdropFilter: 'blur(12px)',
    borderBottom: `1px solid ${isScrolled ? alpha('#fff', 0.1) : 'transparent'}`,
    transition: 'all 0.3s ease',
    boxShadow: isScrolled ? '0 4px 20px rgba(0,0,0,0.2)' : 'none',
  };

  if (!isAuthenticated) {
    return (
      <LayoutProvider paddingX={privateLayoutPadding}>
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', background: '#0f172a' }}>
          <CssBaseline />
          <AppBar position="sticky" sx={appBarStyles} elevation={0}>
            <Container maxWidth={false} sx={{ px: privateLayoutPadding }}>
              <Toolbar disableGutters>
                <Typography variant="h6" component={RouterLink} to="/" sx={{ display: 'flex', alignItems: 'center', fontWeight: 700, color: 'text.primary', textDecoration: 'none', flexGrow: 1 }}>
                  <Box component="img" src="/logo.png" sx={{ height: 32, mr: 1.5, filter: 'drop-shadow(0 0 5px rgba(56,189,248,0.4))' }} alt="PROAPricing Logo" />
                  PROAPricing
                </Typography>
                <Button component={RouterLink} to="/login" variant="outlined" sx={{ color: 'text.primary', borderColor: alpha(theme.palette.text.primary, 0.5), borderRadius: 50 }}>
                  Iniciar sesión
                </Button>
              </Toolbar>
            </Container>
          </AppBar>
          <Container component="main" maxWidth={false} sx={{ flexGrow: 1, p: 0 }}>
            <Outlet />
          </Container>
          <Footer />
        </Box>
      </LayoutProvider>
    );
  }

  // PRC-034: menú de la cuenta (cabecera, Cuenta, Ayuda, Cerrar sesión).
  const accountMenu = (
    <PricingAccountMenu
      anchorPosition={userMenuPosition}
      onClose={handleCloseUserMenu}
      userName={userName}
      userEmail={userEmail}
      planLabel={planLabel}
      isAdmin={isPricingAdmin}
      firstStepsPending={firstStepsPending}
      progress={onboarding ? `${onboarding.done}/${onboarding.total}` : undefined}
      onNavigate={goTo}
      onLogout={() => { void handleLogout(); }}
      zIndex={shouldUseCompactSidebar ? 1402 : 1300}
    />
  );

  return (
    // PRC-031: en Pricing, los diálogos aparecen con un ligero zoom y fundido.
    <ThemeProvider theme={pricingDialogTheme}>
    <LayoutProvider paddingX={privateLayoutPadding}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: '#081120',
          color: '#f8fafc',
          pl: shouldUseCompactSidebar ? { xs: 2, md: 3, xl: 4 } : 0,
          // PRC-044: el margen lateral del contenido lo pone el marco de página (PRC-035, pageFrameSx).
          pr: 0,
          py: { xs: 2.5, md: 3 },
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: shouldUseCompactSidebar ? '1fr' : '72px minmax(0, 1fr)',
            gap: shouldUseCompactSidebar ? 3 : 2.5,
            alignItems: 'start',
          }}
        >
          <Button
            variant="outlined"
            startIcon={<MenuRounded />}
            onClick={() => setMobileSidebarOpen(true)}
            aria-label="Abrir navegación"
            aria-expanded={mobileSidebarOpen}
            sx={{
              display: shouldUseCompactSidebar ? 'inline-flex' : 'none',
              justifySelf: 'start',
              color: '#bae6fd',
              borderColor: alpha('#38bdf8', 0.28),
              fontWeight: 900,
              textTransform: 'none',
            }}
          >
            Menú
          </Button>

          <Box
            onClick={() => setMobileSidebarOpen(false)}
            aria-hidden="true"
            sx={{
              display: shouldUseCompactSidebar && mobileSidebarOpen ? 'block' : 'none',
              position: 'fixed',
              inset: 0,
              zIndex: 1300,
              bgcolor: 'rgba(2,6,23,0.68)',
              backdropFilter: 'blur(3px)',
            }}
          />

          <Box
            component="nav"
            aria-label="Navegación principal de Pricing"
            onMouseEnter={() => {
              if (!shouldUseCompactSidebar) setDesktopSidebarOpen(true);
            }}
            onMouseLeave={() => {
              if (!shouldUseCompactSidebar && !userMenuPosition) {
                setDesktopSidebarOpen(false);
              }
            }}
            onFocusCapture={() => {
              if (!shouldUseCompactSidebar) setDesktopSidebarOpen(true);
            }}
            onBlurCapture={(event) => {
              if (!shouldUseCompactSidebar && !event.currentTarget.contains(event.relatedTarget as Node | null) && !userMenuPosition) {
                setDesktopSidebarOpen(false);
              }
            }}
            sx={{
              border: `1px solid ${alpha('#94a3b8', 0.16)}`,
              bgcolor: 'rgba(8,18,36,0.97)',
              backdropFilter: 'blur(12px)',
              borderRadius: shouldUseCompactSidebar ? 0 : 4,
              p: shouldUseCompactSidebar ? 1.5 : isDesktopSidebarExpanded ? 1.5 : 1,
              minHeight: shouldUseCompactSidebar ? '100vh' : 'calc(100vh - 48px)',
              display: shouldUseCompactSidebar ? (mobileSidebarOpen ? 'flex' : 'none') : 'flex',
              flexDirection: 'column',
              position: shouldUseCompactSidebar ? 'fixed' : 'sticky',
              top: shouldUseCompactSidebar ? 0 : 24,
              left: shouldUseCompactSidebar ? 0 : 'auto',
              bottom: shouldUseCompactSidebar ? 0 : 'auto',
              width: shouldUseCompactSidebar ? 'min(88vw, 340px)' : isDesktopSidebarExpanded ? 300 : 72,
              maxHeight: shouldUseCompactSidebar ? '100vh' : 'none',
              overflowX: shouldUseCompactSidebar ? 'visible' : 'hidden',
              overflowY: shouldUseCompactSidebar ? 'auto' : 'visible',
              transition: shouldUseCompactSidebar ? undefined : 'width 0.18s ease, padding 0.18s ease, box-shadow 0.16s ease',
              boxShadow: !shouldUseCompactSidebar && isDesktopSidebarExpanded
                ? '0 24px 60px rgba(2,6,23,0.46)' : 'none',
              zIndex: shouldUseCompactSidebar ? 1301 : 1200,
            }}
          >
            {/* Identical collapsed-brand treatment and image as Industrial Cost. */}
            <Box
              sx={{
                display: !shouldUseCompactSidebar && !isDesktopSidebarExpanded ? 'flex' : 'none',
                flexDirection: 'column',
                alignItems: 'center',
                minHeight: 'calc(100vh - 80px)',
                width: '100%',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  width: '100%',
                  pt: 0.75,
                  pb: 2,
                  borderBottom: `1px solid ${alpha('#94a3b8', 0.12)}`,
                }}
              >
                <Box
                  component="img"
                  src="/logo.png"
                  alt="PROAPricing"
                  sx={{
                    height: 30,
                    width: 'auto',
                    objectFit: 'contain',
                    flexShrink: 0,
                    filter: 'drop-shadow(0 0 8px rgba(56,189,248,0.3))',
                  }}
                />
              </Box>
              <Box sx={{ pt: 2, flex: 1, width: '100%', minWidth: 0 }}>
                <Typography
                  variant="caption"
                  sx={{ display: 'block', px: 1, pb: 1.25, visibility: 'hidden', pointerEvents: 'none', userSelect: 'none' }}
                >
                  Navegación
                </Typography>
                <List disablePadding sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                  {/* PRC-045: plegado, un icono por página con la activa resaltada. */}
                  {navSections.flatMap((section) => section.items).map((item) => (
                    <PricingRailButton
                      key={item.route}
                      label={item.badge ? `${item.label} (${item.badge})` : item.label}
                      icon={item.icon}
                      selected={item.selected}
                      onClick={() => goTo(item.route)}
                    />
                  ))}
                </List>
              </Box>
              <Box
                sx={{
                  mt: 2,
                  pt: 1.5,
                  borderTop: `1px solid ${alpha('#94a3b8', 0.12)}`,
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <Tooltip title={userName} placement="right">
                <IconButton
                  aria-label="Usuario"
                  aria-current={userMenuActive ? 'page' : undefined}
                  onClick={(event) => handleOpenUserMenu(event)}
                  sx={{
                    width: 46,
                    height: 46,
                    p: 0,
                    borderRadius: 2.5,
                    bgcolor: userMenuActive ? alpha('#38bdf8', 0.16) : alpha('#ffffff', 0.025),
                    border: `1px solid ${userMenuActive ? alpha('#38bdf8', 0.24) : alpha('#94a3b8', 0.1)}`,
                    transition: 'all 0.18s ease',
                    '&:hover': {
                      bgcolor: alpha('#ffffff', 0.05),
                      borderColor: alpha('#38bdf8', 0.22),
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 34,
                      height: 34,
                      bgcolor: alpha('#38bdf8', 0.1),
                      color: '#38bdf8',
                      border: `1px solid ${alpha('#38bdf8', 0.3)}`,
                    }}
                  >
                    <AccountCircle fontSize="medium" />
                  </Avatar>
                </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* Expanded/mobile brand: the SAME image, sizing and typography as Industrial Cost. */}
            <Box
              sx={{
                display: shouldUseCompactSidebar || isDesktopSidebarExpanded ? 'block' : 'none',
                opacity: isDesktopSidebarContentVisible ? 1 : 0,
                transform: isDesktopSidebarContentVisible ? 'translateX(0)' : 'translateX(-4px)',
                transition: shouldUseCompactSidebar ? undefined : 'opacity 0.14s ease, transform 0.14s ease',
                pointerEvents: isDesktopSidebarContentVisible ? 'auto' : 'none',
                px: 1.25,
                pt: 0.5,
                pb: 2.25,
                borderBottom: `1px solid ${alpha('#94a3b8', 0.12)}`,
              }}
            >
              <Stack spacing={0.75}>
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <Box
                    component="img"
                    src="/logo.png"
                    alt="PROAPricing"
                    sx={{
                      height: 30,
                      width: 'auto',
                      objectFit: 'contain',
                      flexShrink: 0,
                      filter: 'drop-shadow(0 0 8px rgba(56,189,248,0.3))',
                    }}
                  />
                  <Typography
                    sx={{
                      color: '#f8fafc',
                      fontWeight: 900,
                      letterSpacing: '-0.6px',
                      fontSize: '1.18rem',
                      lineHeight: 1,
                    }}
                  >
                    PROAPricing
                  </Typography>
                  <IconButton
                    aria-label="Cerrar menú"
                    onClick={() => setMobileSidebarOpen(false)}
                    sx={{ display: shouldUseCompactSidebar ? 'inline-flex' : 'none', ml: 'auto', color: '#94a3b8' }}
                  >
                    <CloseRounded />
                  </IconButton>
                </Stack>
                <Typography
                  sx={{
                    pl: 5.25,
                    color: '#64748b',
                    fontWeight: 800,
                    letterSpacing: 0.8,
                    fontSize: '0.68rem',
                    lineHeight: 1,
                    textTransform: 'uppercase',
                  }}
                >
                  Pricing System
                </Typography>
              </Stack>
            </Box>

            <Box
              sx={{
                display: shouldUseCompactSidebar || isDesktopSidebarExpanded ? 'block' : 'none',
                opacity: isDesktopSidebarContentVisible ? 1 : 0,
                transform: isDesktopSidebarContentVisible ? 'translateX(0)' : 'translateX(-4px)',
                transition: shouldUseCompactSidebar ? undefined : 'opacity 0.14s ease, transform 0.14s ease',
                pointerEvents: isDesktopSidebarContentVisible ? 'auto' : 'none',
                pt: 2,
                flex: 1,
              }}
            >
              {navSections.map((section, index) => (
                <Box key={section.title} sx={{ mt: index === 0 ? 0 : 1.5 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      px: 1,
                      pb: 0.75,
                      color: '#64748b',
                      fontWeight: 800,
                      letterSpacing: 1.1,
                      textTransform: 'uppercase',
                    }}
                  >
                    {section.title}
                  </Typography>
                  <List disablePadding>
                    {section.items.map((item) => (
                      <PricingNavItem
                        key={item.route}
                        label={item.label}
                        icon={item.icon}
                        badge={item.badge}
                        selected={item.selected}
                        onClick={() => goTo(item.route)}
                      />
                    ))}
                  </List>
                </Box>
              ))}
            </Box>

            <Box
              sx={{
                display: shouldUseCompactSidebar || isDesktopSidebarExpanded ? 'block' : 'none',
                opacity: isDesktopSidebarContentVisible ? 1 : 0,
                transform: isDesktopSidebarContentVisible ? 'translateX(0)' : 'translateX(-4px)',
                transition: shouldUseCompactSidebar ? undefined : 'opacity 0.14s ease, transform 0.14s ease',
                pointerEvents: isDesktopSidebarContentVisible ? 'auto' : 'none',
                mt: 2,
                pt: 1.5,
                borderTop: `1px solid ${alpha('#94a3b8', 0.12)}`,
              }}
            >
              <ListItemButton
                selected={userMenuActive}
                onClick={(event) => handleOpenUserMenu(event)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  px: 1.25,
                  py: 1.25,
                  borderRadius: 2.5,
                  bgcolor: alpha('#ffffff', 0.025),
                  border: `1px solid ${alpha('#94a3b8', 0.1)}`,
                  transition: 'all 0.18s ease',
                  '&:hover, &.Mui-selected, &.Mui-selected:hover': {
                    bgcolor: alpha('#ffffff', 0.05),
                    border: `1px solid ${alpha('#38bdf8', 0.22)}`,
                  },
                }}
              >
                <Avatar
                  sx={{
                    width: 34,
                    height: 34,
                    bgcolor: alpha('#38bdf8', 0.1),
                    color: '#38bdf8',
                    border: `1px solid ${alpha('#38bdf8', 0.3)}`,
                  }}
                >
                  <AccountCircle fontSize="medium" />
                </Avatar>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography noWrap sx={{ color: '#f8fafc', fontWeight: 850, fontSize: '0.9rem', lineHeight: 1.2 }}>
                    {userName}
                  </Typography>
                  <Typography noWrap sx={{ color: '#64748b', fontWeight: 700, fontSize: '0.76rem', lineHeight: 1.2 }}>
                    {userSubtitle}
                  </Typography>
                </Box>
              </ListItemButton>
            </Box>
            {accountMenu}
          </Box>

          {/* PRC-032: cifras de ancho fijo en todas las tablas de Pricing. */}
          <Box
            component="main"
            sx={{
              minWidth: 0,
              width: '100%',
              '& .MuiTableCell-root': { fontVariantNumeric: 'tabular-nums' },
              // PRC-031: fundido corto al cambiar de pestaña (el foco visible va en GlobalStyles).
              '& [role="tabpanel"]': {
                animation: 'proaTabFade 180ms cubic-bezier(0.22, 1, 0.36, 1)',
                '@keyframes proaTabFade': { from: { opacity: 0 }, to: { opacity: 1 } },
                '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
              },
            }}
          >
            <TrialBanner />
            <CancelledSubscriptionBanner />
            {/* PRC-031: foco visible con el teclado en toda la aplicación de Pricing (también menú, diálogos y cajones). */}
            <GlobalStyles styles={{
              ':focus-visible': { outline: '2px solid #38bdf8', outlineOffset: '2px' },
              // PRC-036: ningún campo de Pricing se pinta de blanco al autocompletar el navegador.
              'input:-webkit-autofill, input:-webkit-autofill:hover, input:-webkit-autofill:focus, textarea:-webkit-autofill, select:-webkit-autofill': {
                WebkitBoxShadow: '0 0 0 1000px #0b1424 inset',
                WebkitTextFillColor: '#f8fafc',
                caretColor: '#f8fafc',
                transition: 'background-color 9999s ease-out 0s',
              },
            }} />
            {/* PRC-031: solo el contenido se anima; el menú y las bandas, no. */}
            <PageTransition />
          </Box>
        </Box>
      </Box>
    </LayoutProvider>
    </ThemeProvider>
  );
};

export default MainLayout;
